import {put, call, takeLatest} from 'redux-saga/effects';

import {
  LOGIN_REQUEST,
  ON_MESSAGE,
  GET_INVITATION_DATA_REQUEST,
  ON_CODE_ERROR,
  SIGNUP_REQUEST,
  ON_API_ERROR,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_SUCCESS,
  RESET_PASSWORD_CODE_REQUEST,
  RESET_PASSWORD_REQUEST,
  CHANGE_PASSWORD_REQUEST,
  UPDATE_PROFILE_REQUEST,
  AGREEMENT_REQUEST,
  SET_LANGUAGE,
  SAVE_LANGUAGE,
} from '../../actions/types';
import {
  onInvitationDataSuccess,
  onAuthSuccess,
  onPasswordCodeSuccess,
  onProfileUpdated,
} from '../../actions/auth';
import Api from '../../utils/api';
import {forwardTo} from "../../utils";

function loginApi(params) {
  return Api.post('/login', params);
}

function signUpApi(params) {
  return Api.post('/sign-up', params);
}

function getInvitationDataApi(invitationId) {
  return Api.get(`/invitation/${invitationId}`);
}

function resetPasswordRequestApi(email, isReset) {
  return Api.post('/reset-password/request', {email, isReset});
}

function checkPasswordCodeApi(code) {
  return Api.get(`/reset-password/${code}`);
}

function resetPasswordApi({password, passwordResetCode, id}) {
  return Api.post(`/reset-password/update`, {password, passwordResetCode, id});
}

function updateProfileApi(id, data) {
  return Api.post(`/users/${id}`, data);
}

function agreementApi() {
  return Api.post('/users/agree');
}

function* onSuccess(message) {
  yield put({
    type: ON_MESSAGE,
    message: message,
    messageType: 'success',
  });
}

function* onError(error) {
  yield put({
    type: ON_MESSAGE,
    message: error,
    messageType: 'error',
  });

  yield put({
    type: ON_API_ERROR,
  });
}

function* forgotPasswordRequest({email, isReset = true}) {
  try {
    yield call(resetPasswordRequestApi, email, isReset);
    yield put({type: FORGOT_PASSWORD_SUCCESS});
  } catch (error) {
    yield onError(error);
  }
}

function* checkPasswordCodeSaga({code}) {
  try {
    const {user} = yield call(checkPasswordCodeApi, code);

    yield put(onPasswordCodeSuccess(user))

  } catch (error) {
    const {status} = error;

    if (status === 400) {
      yield put({type: ON_CODE_ERROR});
      return;
    }

    yield onError(error);
  }
}

function* resetPasswordSaga({password, passwordResetCode, id}) {
  try {
    yield call(resetPasswordApi, {password, passwordResetCode, id});

    yield put(onPasswordCodeSuccess())

    yield onSuccess('Your password was changed');
    forwardTo({location: '/auth/login'});

  } catch (error) {
    yield onError(error);
  }
}

function* changePasswordSaga({password, passwordResetCode, id}) {
  try {
    yield call(resetPasswordApi, {password, passwordResetCode, id});

    yield onSuccess('Your password was changed');
    forwardTo({location: '/dashboard'});

  } catch (error) {
    yield onError(error);
  }
}

function* getInvitationData({invitationId}) {
  try {
    const {user} = yield call(getInvitationDataApi, invitationId);

    yield put(onInvitationDataSuccess(user));
  } catch (error) {
    const {status} = error;

    if (status === 400) {
      yield put({type: ON_CODE_ERROR});
      return;
    }

    yield onError(error);
  }
}

function* logIn(action) {
  try {
    const {email, password} = action;
    const authData = yield call(loginApi, {email, password});

    yield put(onAuthSuccess(authData));
  } catch (error) {
    yield onError(error);
  }
}

function* signUp(action) {
  try {
    const {id, password, invitationId: invitationCode} = action;
    const authData = yield call(signUpApi, {id, password, invitationCode});

    yield put(onAuthSuccess(authData));
  } catch (error) {
    yield onError(error);
  }
}

function* updateProfileSaga({id, data}) {
  try {
    const profile = yield call(updateProfileApi, id, data);

    yield onSuccess('Profile was successfully updated.');
    yield put(onProfileUpdated(profile));
  } catch (error) {
    yield onError(error);
  }
}

function* agreement() {
  try {
    const profile = yield call(agreementApi);

    yield put(onProfileUpdated(profile));

    forwardTo({location: '/dashboard'})
  } catch (error) {
    yield onError(error);
  }
}

function* setLanguage(action) {
  try {
    const { lng } = action;

    yield put({type: SAVE_LANGUAGE, lng});
  } catch (error) {
    yield onError(error);
  }
}

export function* getLogInWatcher() {
  yield takeLatest(LOGIN_REQUEST, logIn);
}

export function* getSingUpWatcher() {
  yield takeLatest(SIGNUP_REQUEST, signUp);
}

export function* getInvitationDataWatcher() {
  yield takeLatest(GET_INVITATION_DATA_REQUEST, getInvitationData);
}

export function* forgotPasswordRequestWatcher() {
  yield takeLatest(FORGOT_PASSWORD_REQUEST, forgotPasswordRequest);
}

export function* checkResetCodeWatcher() {
  yield takeLatest(RESET_PASSWORD_CODE_REQUEST, checkPasswordCodeSaga);
}

export function* resetPasswordWatcher() {
  yield takeLatest(RESET_PASSWORD_REQUEST, resetPasswordSaga);
}

export function* changePasswordWatcher() {
  yield takeLatest(CHANGE_PASSWORD_REQUEST, changePasswordSaga);
}

export function* updateProfileWatcher() {
  yield takeLatest(UPDATE_PROFILE_REQUEST, updateProfileSaga);
}

export function* agreementWatcher() {
  yield takeLatest(AGREEMENT_REQUEST, agreement);
}

export function* setLanguageWatcher() {
  yield takeLatest(SET_LANGUAGE, setLanguage);
}